import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, Copy, X, Check } from "lucide-react";
import { toast } from "sonner";

const NAME_KEY = "meera-studio-artist-name";

const readSavedName = () => {
  if (typeof window === "undefined") return "";
  try {
    return window.localStorage.getItem(NAME_KEY) || "";
  } catch {
    return "";
  }
};

/**
 * A reusable "Share my artwork" button + modal used by every studio module.
 * Generates a friendly share message + link to the relevant studio page,
 * uses the native Web Share API when available, otherwise copies to clipboard.
 */
export default function ShareArtwork({ artFormId, artName, accent, testIdPrefix }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(readSavedName);
  const [copied, setCopied] = useState(false);

  const closeModal = () => {
    setOpen(false);
    setCopied(false);
  };

  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const url = `${origin}/studio/${artFormId}`;
  const cleanName = name.trim();
  const shareText = cleanName
    ? `${cleanName} just made a beautiful ${artName} in Meera's Art Studio. Come make your own →`
    : `I just made a beautiful ${artName} in Meera's Art Studio. Come make your own →`;
  const fullMessage = `${shareText} ${url}`;

  const saveName = (v) => {
    setName(v);
    try {
      if (v.trim()) window.localStorage.setItem(NAME_KEY, v.trim());
    } catch (err) {
      console.warn("ShareArtwork: localStorage write failed", err);
    }
  };

  const doNativeShare = async () => {
    if (typeof navigator === "undefined" || !navigator.share) return false;
    try {
      await navigator.share({
        title: `${artName} from Meera's Art Studio`,
        text: shareText,
        url,
      });
      return true;
    } catch (err) {
      // user cancelled or failed; treat as no-op
      return false;
    }
  };

  const onShare = async () => {
    const shared = await doNativeShare();
    if (shared) {
      toast.success("Shared!");
      closeModal();
    }
  };

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(fullMessage);
      setCopied(true);
      toast.success("Copied — paste it anywhere to share.");
    } catch {
      toast.error("Couldn't copy. You can select the text manually.");
    }
  };

  const canNativeShare =
    typeof navigator !== "undefined" && typeof navigator.share === "function";

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`${testIdPrefix}-share-open`}
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border-2 text-sm font-medium tracking-wide transition-colors duration-300"
        style={{
          borderColor: accent,
          color: accent,
          background: "transparent",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background = accent;
          e.currentTarget.style.color = "#F9F6F0";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background = "transparent";
          e.currentTarget.style.color = accent;
        }}
      >
        <Share2 size={14} /> Share my artwork
      </button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#1E2A4F]/60 backdrop-blur-sm"
            onClick={closeModal}
            data-testid={`${testIdPrefix}-share-modal`}
          >
            <motion.div
              initial={{ y: 24, scale: 0.96, opacity: 0 }}
              animate={{ y: 0, scale: 1, opacity: 1 }}
              exit={{ y: 24, scale: 0.96, opacity: 0 }}
              transition={{ duration: 0.25, ease: [0.22, 1, 0.36, 1] }}
              onClick={(e) => e.stopPropagation()}
              className="bg-[#F9F6F0] rounded-2xl shadow-2xl border-2 border-[#E5DFD3] max-w-md w-full p-6 sm:p-8 relative"
              style={{ borderColor: accent }}
            >
              <button
                onClick={closeModal}
                data-testid={`${testIdPrefix}-share-close`}
                aria-label="Close"
                className="absolute top-4 right-4 w-8 h-8 rounded-full hover:bg-[#E5DFD3] flex items-center justify-center text-[#5C6A8F]"
              >
                <X size={16} />
              </button>

              <span
                className="inline-flex items-center gap-2"
                style={{ color: accent }}
              >
                <Share2 size={16} />
                <span className="overline" style={{ color: accent }}>
                  Share your art
                </span>
              </span>
              <h3 className="font-display text-3xl text-[#1E2A4F] mt-2 leading-tight">
                Show off your{" "}
                <em className="italic font-light">{artName.toLowerCase()}</em>.
              </h3>
              <p className="text-sm text-[#5C6A8F] mt-3 leading-relaxed">
                Send a sweet little note + studio link to a grandparent, teacher, or
                friend — it might just bring another little artist to Meera&apos;s world.
              </p>

              <label className="block mt-6">
                <span className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F]">
                  Your name (optional)
                </span>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => saveName(e.target.value)}
                  data-testid={`${testIdPrefix}-share-name`}
                  placeholder="e.g. Aanya"
                  className="mt-2 w-full bg-transparent border-b border-[#1E2A4F]/20 focus:border-[#1E2A4F] focus:outline-none py-2 text-[#1E2A4F] transition-colors"
                />
              </label>

              <div
                className="mt-6 p-4 rounded-xl border border-[#E5DFD3] bg-white"
                data-testid={`${testIdPrefix}-share-preview`}
              >
                <p className="text-[10px] tracking-[0.24em] uppercase text-[#5C6A8F] mb-2">
                  Preview
                </p>
                <p className="text-sm text-[#1E2A4F] leading-relaxed">
                  {shareText}
                </p>
                <p
                  className="text-xs mt-2 break-all"
                  style={{ color: accent }}
                >
                  {url}
                </p>
              </div>

              <div className="mt-6 grid grid-cols-2 gap-3">
                <button
                  onClick={onCopy}
                  data-testid={`${testIdPrefix}-share-copy`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full border border-[#1E2A4F]/20 text-[#1E2A4F] hover:bg-[#1E2A4F] hover:text-[#F9F6F0] transition-colors duration-300 text-sm"
                >
                  {copied ? <Check size={14} /> : <Copy size={14} />}
                  {copied ? "Copied" : "Copy text"}
                </button>
                <button
                  onClick={onShare}
                  disabled={!canNativeShare}
                  data-testid={`${testIdPrefix}-share-native`}
                  className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-full text-[#F9F6F0] text-sm font-medium transition-colors duration-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{ background: accent }}
                  title={canNativeShare ? "Open share sheet" : "Native sharing not available on this device — use Copy"}
                >
                  <Share2 size={14} /> Share…
                </button>
              </div>
              {!canNativeShare && (
                <p className="text-[11px] text-[#5C6A8F] mt-3 text-center">
                  On desktop browsers, use <strong>Copy text</strong> then paste it
                  into your message.
                </p>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
